import * as Sentry from "@sentry/react";
import { onAuthStateChanged } from "firebase/auth";
import {
  createContext,
  useContext,
  useEffect,
  useReducer,
  useState,
} from "react";
import { LoadingScreen } from "../../components/loading-screen";
import { useKiosk } from "../../kiosk/kiosk-context";
import { KioskTerminalView } from "../../kiosk/kiosk-terminal-view";
import { auth } from "../config";
import {
  firebaseAuthBootstrapDependencies,
  prepareAuthForAppLaunch,
} from "../kiosk-auth-bootstrap";

import { AuthActionKind, authReducer, AuthState } from "./auth-context.reducer";

const initialState: AuthState = {
  user: null,
  loading: true,
};

export const authContext = createContext<AuthState>(initialState);

export type AuthProviderProps = {
  children: React.ReactNode;
};

export const AuthProvider = (props: AuthProviderProps) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const [bootstrapFailed, setBootstrapFailed] = useState(false);
  const { isKiosk } = useKiosk();

  useEffect(() => {
    let isActive = true;
    let unsubscribe: (() => void) | undefined;

    const initializeAuth = async () => {
      try {
        await prepareAuthForAppLaunch(
          isKiosk,
          firebaseAuthBootstrapDependencies(auth),
        );
        if (!isActive) return;

        unsubscribe = onAuthStateChanged(auth, async (user) => {
          if (user) {
            dispatch({ type: AuthActionKind.LOGGED_IN, user });
            Sentry.setUser({ id: user.uid });
          } else {
            dispatch({ type: AuthActionKind.LOGGED_OUT });
            Sentry.setUser(null);
          }
        });
      } catch (error) {
        if (!isActive) return;
        Sentry.captureException(error);
        setBootstrapFailed(true);
      }
    };

    void initializeAuth();

    return () => {
      isActive = false;
      unsubscribe?.();
    };
  }, [isKiosk]);

  if (bootstrapFailed) {
    return (
      <KioskTerminalView
        titleKey="kiosk.authInitialization.title"
        bodyKey="kiosk.authInitialization.body"
        actionKey="kiosk.authInitialization.retry"
        onAction={() => window.location.reload()}
      />
    );
  }

  if (state.loading) {
    return <LoadingScreen />;
  }

  return (
    <authContext.Provider value={state}>{props.children}</authContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(authContext);
  return { ...context };
};
